import React, { useMemo } from 'react';

import { Button, Center, Group, Loader, Table, px, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import PayRateSelect from 'components/form-fields/select-fields/PayRateSelect';
import useAppointmentCoachRoles from 'hooks/appointmentCoachRole/useAppointmentCoachRoles';
import useAppointmentPayRateLookups from 'hooks/appointmentPayRateLookup/useAppointmentPayRateLookups';
import useUpdateAppointmentPayRateLookup from 'hooks/appointmentPayRateLookup/useUpdateAppointmentPayRateLookup';
import useAppointmentTypes from 'hooks/appointmentType/useAppointmentTypes';
import useAllPages from 'hooks/useAllPages';
import { AppointmentPayRateLookup } from 'types';
import { getAppointmentTypeRoleToPayRateLookup } from 'utils/payRates.helpers';

import AppointmentCoachRoleTableHeader from './components/AppointmentCoachRoleTableHeader';
import ModalCreateAppointmentCoachRole from './components/ModalCreateAppointmentCoachRole';
import useAssignPayRatesTableStyles from './components/styles';

interface IAppointmentPayRateLookupTableProps {
  permission?: boolean;
  staff: string;
}

const AppointmentPayRateLookupTable = ({
  staff,
  permission = true,
}: IAppointmentPayRateLookupTableProps): JSX.Element => {
  const { classes } = useAssignPayRatesTableStyles();
  const theme = useMantineTheme();
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const updateMutation = useUpdateAppointmentPayRateLookup();

  const {
    items: lookups,
    isLoading: payRateLookupsLoading,
    isSuccess: payRateLookupsSuccess,
  } = useAllPages(useAppointmentPayRateLookups({ staff, limit: '100' }));
  const {
    items: coachRoles,
    isLoading: coachRolesLoading,
    isSuccess: coachRolesSuccess,
  } = useAllPages(useAppointmentCoachRoles({ limit: '100' }));
  const {
    items: appointmentTypes,
    isLoading: appointmentTypesLoading,
    isSuccess: appointmentTypesSuccess,
  } = useAllPages(useAppointmentTypes({ limit: '100' }));

  const appointmentTypeRoleToPayRateLookup = useMemo(
    () => getAppointmentTypeRoleToPayRateLookup(lookups),
    [lookups],
  );

  const handleChangePayRate = (payRateId: string | null, values: AppointmentPayRateLookup): void => {
    if (!!values) {
      const payload = {
        ...values,
        payRate: payRateId,
      };
      updateMutation.mutate(payload);
    }
  };

  return (
    <>
      {(payRateLookupsLoading || coachRolesLoading || appointmentTypesLoading) && (
        <Center my='xl'>
          <Loader />
        </Center>
      )}
      {payRateLookupsSuccess && coachRolesSuccess && appointmentTypesSuccess && (
        <Group align='top' className={classes.table}>
          <div style={{ overflowX: 'auto', maxWidth: '100%', width: '100%' }}>
            <Table withColumnBorders withBorder verticalSpacing='md' sx={{ minWidth: 'max-content' }}>
              <thead>
                <tr>
                  <th style={{ width: `${px(theme.spacing.xl) * 10}px`, background: theme.colors.gray[3] }}>
                    Appointment Rates
                  </th>
                  {coachRoles.map((role, i) => (
                    <AppointmentCoachRoleTableHeader key={i} role={role} permission={permission} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {appointmentTypes.map((appointmentType, i) => (
                  <tr key={appointmentType.appointmentTypeId}>
                    <td style={{ width: `${px(theme.spacing.xl) * 10}px` }}>{appointmentType.name}</td>
                    {coachRoles.map((role, y) => (
                      <td
                        key={`${appointmentType.appointmentTypeId}-${role.appointmentCoachRoleId}`}
                        style={{ width: `${px(theme.spacing.xl) * 10}px` }}
                      >
                        <PayRateSelect
                          cyData={`appointmentPayRateSelect${i}${y}`}
                          disabled={!permission}
                          value={
                            appointmentTypeRoleToPayRateLookup[
                              `${appointmentType.appointmentTypeId}-${role.appointmentCoachRoleId}`
                            ]?.payRate
                          }
                          onChange={event =>
                            handleChangePayRate(
                              event,
                              appointmentTypeRoleToPayRateLookup[
                                `${appointmentType.appointmentTypeId}-${role.appointmentCoachRoleId}`
                              ],
                            )
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <Button
            leftIcon={<IconPlus size={theme.fontSizes.sm} />}
            onClick={openCreateModal}
            disabled={!permission}
            data-cy='createAppointmentCoachRoleButton'
          >
            Create Coach Role For Appointment
          </Button>
        </Group>
      )}
      <ModalCreateAppointmentCoachRole opened={createModalOpened} handleClose={closeCreateModal} />
    </>
  );
};

export default AppointmentPayRateLookupTable;
